import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Stack,
  Checkbox,
  FormControlLabel,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  ListItemText,
} from "@mui/material";
import {
  Search as SearchIcon,
} from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { openShipmentModal } from "../../store/adminSlice";
import { DataTableComponent } from "../../components/EmailDataTableComponent/DataTableComponent";
import type { DataTableColumn } from "../../components/EmailDataTableComponent/DataTableComponent";
import { useSnackbar } from "../../components/DobbyGenericPage";
import { apiClient } from "../../utils/apiClient";
import type {
  ListShipmentsApiRequest,
  ListShipmentsApiResponse,
  ShipmentApi,
  ShipmentFeedError,
  ShipmentFeedErrorTypes,
  UpdateShipmentStatusRequest,
} from "@services/shipments/types";
import { ShipmentAuditModal } from "./ShipmentAuditModal";
import { EditShipmentModal } from "./EditShipmentModal";
import { ShipmentErrorChip } from "./ShipmentErrorChip";
import { ShipmentDashboardErrors } from "./ShipmentDashboardErrors";
import { AdminNotesCell } from "./AdminNotesCell";
import AdminPage from "../AdminPage";
import { getShipmentDetailsFromProperties } from "./shipmentPropertiesUtils";
import { ShipmentActionsMenu } from "./ShipmentActionsMenu";
import { SimulateRemindersDialog } from "./SimulateRemindersDialog";
import { set } from "date-fns";

const ROW_HEIGHT = 120;

interface ShipmentsTableRow extends ShipmentApi {
  // Extended with computed fields for display
  propertiesText: string;
  orgName?: string;
  isInvalid: boolean;
  errors: ShipmentFeedError[];
  adminNotes?: string;
}

const ERROR_TYPES: ShipmentFeedErrorTypes[] = [
  "unique-property-violation",
  "property-validation",
  "merge-conflict",
  "other",
];
export function ShipmentsDashboardMonitorPage() {
  return (
    <AdminPage title="Shipments Dashboard Monitor">
      <ShipmentsDashboardMonitor />
    </AdminPage>
  );
}

export function ShipmentsDashboardMonitor() {
  const { showSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [shipments, setShipments] = useState<ShipmentsTableRow[]>([]);
  const [shipmentsTotalCount, setShipmentsTotalCount] = useState(0);
  const [shipmentsActiveCount, setShipmentsActiveCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [paginationOffset, setPaginationOffset] = useState(0);
  const [includeMerged, setIncludeMerged] = useState(true);
  const [includeInvalid, setIncludeInvalid] = useState(true);
  const [selectedErrorTypes, setSelectedErrorTypes] = useState<
    ShipmentFeedErrorTypes[]
  >([]);
  const [includeResolvedErrors, setIncludeResolvedErrors] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [
    clearTriggeredSearchAfterStateUpdate,
    setTriggerSearchAfterStateUpdate,
  ] = useState(false);

  // Get tag from query parameter, default to empty (admin sees all)
  const tag = searchParams.get("tag");
  const TAGS = useMemo(() => (tag ? [tag] : []), [tag]);

  // Get search from query parameter for auto-search
  const searchFromUrl = searchParams.get("search");

  // Modal states
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [simulateDialogOpen, setSimulateDialogOpen] = useState(false);
  const [selectedShipmentId, setSelectedShipmentId] = useState<string>("");

  const transformShipmentToTableRow = useCallback(
    (shipment: ShipmentApi): ShipmentsTableRow => {
      // Ensure properties exist and is an array
      const props = getShipmentDetailsFromProperties(shipment);

      // Format properties as "name: value" lines
      const propertyLines = props.map(
        (prop) => `${prop.label}: ${prop.displayValue}`
      );

      // If more than 6 properties, show first 6 and add "..."
      let propertiesText;
      if (propertyLines.length > 6) {
        propertiesText = propertyLines.slice(0, 6).join("\n") + "\n...";
      } else {
        propertiesText = propertyLines.join("\n");
      }

      return {
        ...shipment,
        adminNotes: shipment.adminNotes || "",
        // Convert date strings to Date objects
        createdAt: shipment.createdAt
          ? new Date(shipment.createdAt)
          : new Date(),
        updatedAt: shipment.updatedAt
          ? new Date(shipment.updatedAt)
          : new Date(),
        propertiesText,
        orgName: shipment.orgName || undefined,
        isInvalid: shipment.flaggedAsInvalid || false,
        errors: shipment.errors || [],
      };
    },
    []
  );

  const fetchShipments = useCallback(
    async (offset: number = 0, search?: string, append: boolean = false) => {
      try {
        if (!append) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }
        setPaginationOffset(offset);

        const requestBody: ListShipmentsApiRequest = {
          offset,
          tags: TAGS, // Use tags from URL parameter
          includeMerged,
          includeInvalid,
          sortBy: "last_update",
          search,
          errorTypes:
            selectedErrorTypes.length > 0 ? selectedErrorTypes : undefined,
          includeResolvedErrors:
            selectedErrorTypes.length > 0 ? includeResolvedErrors : undefined,
        };

        const response = await apiClient.post<ListShipmentsApiResponse>(
          "/shipments/list",
          requestBody
        );

        const transformedShipments = response.shipments.map(
          transformShipmentToTableRow
        );

        if (append) {
          setShipments((prev) => [...prev, ...transformedShipments]);
        } else {
          setShipments(transformedShipments);
        }

        setHasMore(response.hasMore);
        setShipmentsTotalCount(response.total);
        setShipmentsActiveCount(response.activeCount || 0);
      } catch (error) {
        console.error("Error fetching shipments:", error);
        showSnackbar("Failed to load shipments", 3000, "error");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      includeMerged,
      includeInvalid,
      selectedErrorTypes,
      includeResolvedErrors,
      TAGS,
    ]
  );

  // Initial load
  useEffect(() => {
    // Only fetch on initial load, not when filters change
    const initialFetch = async () => {
      try {
        setLoading(true);
        const requestBody: ListShipmentsApiRequest = {
          offset: 0,
          tags: TAGS,
          includeMerged: true,
          includeInvalid: true,
          sortBy: "last_error",
        };
        setPaginationOffset(0);

        const response = await apiClient.post<ListShipmentsApiResponse>(
          "/shipments/list",
          requestBody
        );

        const transformedShipments = response.shipments.map(
          transformShipmentToTableRow
        );

        setShipments(transformedShipments);
        setShipmentsTotalCount(response.total);
        setShipmentsActiveCount(response.activeCount || 0);
        setHasMore(response.hasMore);
      } catch (error) {
        console.error("Error fetching shipments:", error);
        showSnackbar("Failed to load shipments", 3000, "error");
      } finally {
        setLoading(false);
      }
    };

    initialFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [TAGS]);

  // Handle search from URL parameter
  useEffect(() => {
    if (searchFromUrl) {
      setSearchTerm(searchFromUrl);
      fetchShipments(0, searchFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFromUrl]);

  // Handle clear trigger - fetch after state has been cleared
  useEffect(() => {
    if (clearTriggeredSearchAfterStateUpdate) {
      setTriggerSearchAfterStateUpdate(false);
      fetchShipments(0, undefined);
    }
  }, [clearTriggeredSearchAfterStateUpdate, fetchShipments]);

  const handleSearch = useCallback(() => {
    fetchShipments(0, searchTerm || undefined);
  }, [fetchShipments, searchTerm]);

  const handleRefresh = useCallback(() => {
    fetchShipments(paginationOffset, searchTerm);
  }, [fetchShipments, paginationOffset, searchTerm]);

  const handleLoadMore = useCallback(() => {
    if (hasMore && !loadingMore) {
      fetchShipments(shipments.length, searchTerm || undefined, true);
    }
  }, [fetchShipments, hasMore, loadingMore, shipments.length, searchTerm]);

  const handleResolveError = useCallback(
    async (shipmentId: string, errorIds: string[], revert: boolean = false) => {
      try {
        // Use the new API with multiple error IDs
        await apiClient.post("/shipments/resolve_shipment_error", {
          shipmentId,
          errorIds,
          revert,
        });

        // Update the rows locally
        setShipments((prev) =>
          prev.map((shipment) => {
            if (shipment.id === shipmentId) {
              return {
                ...shipment,
                errors: shipment.errors.map((error) =>
                  errorIds.includes(error.id!)
                    ? { ...error, resolved: !revert }
                    : error
                ),
              };
            }
            return shipment;
          })
        );

        const count = errorIds.length;
        showSnackbar(
          revert
            ? `${count} error${count > 1 ? "s" : ""} reverted successfully`
            : `${count} error${count > 1 ? "s" : ""} resolved successfully`,
          3000,
          "info"
        );
      } catch (error) {
        console.error("Error resolving shipment error:", error);
        showSnackbar("Failed to resolve error", 3000, "error");
      }
    },
    [showSnackbar]
  );

  const handleToggleStatus = useCallback(
    async (shipmentId: string, currentStatus: string | null | undefined) => {
      try {
        const newStatus = (!currentStatus || currentStatus === "active") ? "not_active" : "active";
        await apiClient.post<{ status: string; message: string }>(
          "/shipments/update-status",
          { shipmentId, status: newStatus } satisfies UpdateShipmentStatusRequest
        );
        showSnackbar(`Shipment status changed to ${newStatus}`, 3000, "info");
        fetchShipments(paginationOffset, searchTerm || undefined);
      } catch (error) {
        console.error("Error toggling shipment status:", error);
        showSnackbar("Failed to update shipment status", 3000, "error");
      }
    },
    [showSnackbar, fetchShipments, paginationOffset, searchTerm]
  );

  const columns: DataTableColumn<ShipmentsTableRow>[] = useMemo(
    () => [
      {
        columnName: "Last Modified",
        fieldName: "updatedAt",
        renderFn: (row) => {
          const updatedDate = new Date(row.updatedAt).toLocaleString();
          const shipmentData = row;
          const lastErrorTimestamp = shipmentData.lastErrorTimestamp;

          return (
            <Box>
              <Typography variant="body2" sx={{ fontSize: "12px" }}>
                {updatedDate}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "10px",
                  color: "text.secondary",
                  display: "block",
                  mt: 0.5,
                }}
              >
                Last error:{" "}
                {lastErrorTimestamp
                  ? new Date(lastErrorTimestamp).toLocaleString()
                  : "N/A"}
              </Typography>
            </Box>
          );
        },
        width: 160,
        visibleByDefault: true,
      },
      {
        columnName: "Properties",
        fieldName: "propertiesText",
        renderFn: (row) => (
          <Typography
            variant="body2"
            onClick={() => {
              setSelectedShipmentId(row.id);
              setEditModalOpen(true);
            }}
            sx={{
              maxHeight: ROW_HEIGHT - 20,
              whiteSpace: "pre-line",
              fontFamily: "monospace",
              fontSize: "8px",
              maxWidth: 400,
              cursor: "pointer",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            {row.propertiesText}
          </Typography>
        ),
        flex: 2,
        visibleByDefault: true,
      },
      {
        columnName: "Org / Tags",
        renderFn: (row) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {row.orgName && (
              <Typography variant="body2" sx={{ fontSize: "11px", fontWeight: 500 }}>
                {row.orgName}
              </Typography>
            )}
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {row.tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: "10px" }} />
              ))}
            </Box>
          </Box>
        ),
        width: 180,
        visibleByDefault: true,
      },
      {
        columnName: "Status",
        renderFn: (row) => (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {row.isInvalid && <Chip label="Invalid" color="error" size="small" />}
            <Chip
              label={(!row.status || row.status === "active") ? "Active" : "Not Active"}
              color={(!row.status || row.status === "active") ? "success" : "default"}
              size="small"
            />
          </Box>
        ),
        width: 150,
        visibleByDefault: true,
      },
      {
        columnName: "Errors",
        renderFn: (row) => (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              maxHeight: ROW_HEIGHT - 20,
              overflowY: "auto",
            }}
          >
            <ShipmentDashboardErrors
              errors={[...row.errors]}
              onResolve={(errorIds, revert) =>
                handleResolveError(row.id, errorIds, revert)
              }
              shipmentId={row.id}
            />
          </Box>
        ),
        flex: 1,
        visibleByDefault: true,
      },
      {
        columnName: "Notes",
        renderFn: (row) => (
          <Box sx={{ height: ROW_HEIGHT - 20, width: 200 }}>
            <AdminNotesCell
              shipmentId={row.id}
              initialNotes={row.adminNotes}
              onNotesUpdate={(notes) => {
                // Update local state
                setShipments((prev) =>
                  prev.map((shipment) =>
                    shipment.id === row.id
                      ? { ...shipment, adminNotes: notes }
                      : shipment
                  )
                );
              }}
            />
          </Box>
        ),
        width: 200,
        visibleByDefault: true,
      },
      {
        columnName: "Actions",
        renderFn: (row) => (
          <ShipmentActionsMenu
            onAuditLog={() => {
              setSelectedShipmentId(row.id);
              setAuditModalOpen(true);
            }}
            onTimeline={() => {
              dispatch(openShipmentModal(row.id));
            }}
            onEdit={() => {
              setSelectedShipmentId(row.id);
              setEditModalOpen(true);
            }}
            onSimulate={() => {
              setSelectedShipmentId(row.id);
              setSimulateDialogOpen(true);
            }}
            onChangeStatus={() => handleToggleStatus(row.id, row.status)}
          />
        ),
        width: 60,
        visibleByDefault: true,
      },
    ],
    [handleResolveError, handleToggleStatus]
  );

  return (
    <>
      <Box
        sx={{
          padding: "6px",
          height: "calc(100vh - 100px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Search and Filters */}
        <Card elevation={0} sx={{ mb: 2 }}>
          <CardContent>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              flexWrap="wrap"
            >
              <TextField
                size="small"
                label="Search shipments"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                sx={{ minWidth: 300 }}
                placeholder="Search by ID or property values..."
              />

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Filter by Error Types</InputLabel>
                <Select
                  multiple
                  value={selectedErrorTypes}
                  onChange={(event) => {
                    const value = event.target.value;
                    setSelectedErrorTypes(
                      typeof value === "string"
                        ? (value.split(",") as ShipmentFeedErrorTypes[])
                        : value
                    );
                  }}
                  input={<OutlinedInput label="Filter by Error Types" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value
                            .replace(/-/g, " ")
                            .replace(/\b\w/g, (l) => l.toUpperCase())}
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                >
                  {ERROR_TYPES.map((errorType) => (
                    <MenuItem key={errorType} value={errorType}>
                      <Checkbox
                        checked={selectedErrorTypes.includes(errorType)}
                      />
                      <ListItemText
                        primary={errorType
                          .replace(/-/g, " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Show resolved errors checkbox only when error types are selected */}
              {selectedErrorTypes.length > 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeResolvedErrors}
                      onChange={(e) =>
                        setIncludeResolvedErrors(e.target.checked)
                      }
                      size="small"
                    />
                  }
                  label="Include shipments with resolved errors"
                  sx={{ ml: 1, maxWidth: 170 }}
                />
              )}

              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setSearchTerm("");
                  setSelectedErrorTypes([]);
                  setTriggerSearchAfterStateUpdate(true);
                }}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  minWidth: "auto",
                  px: 1,
                  color: "text.secondary",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "primary.main",
                  },
                }}
              >
                clear
              </Button>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeMerged}
                    onChange={(e) => setIncludeMerged(e.target.checked)}
                  />
                }
                label="Include Merged"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeInvalid}
                    onChange={(e) => setIncludeInvalid(e.target.checked)}
                  />
                }
                label="Include Invalid"
              />

              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
              >
                Search
              </Button>

              <Box sx={{ flex: 1 }} />

              {tag && (
                <Chip
                  label={`Showing shipments with tag: ${tag}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ mr: 2 }}
                />
              )}

              <Typography variant="body2" color="text.secondary">
                Total: {shipmentsTotalCount} shipments, {shipmentsActiveCount} active
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        {/* Shipments Table */}
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <DataTableComponent
            isLoading={loading}
            rows={shipments}
            columns={columns}
            keyField="id"
            stickyHeader={true}
            showColumnVisibilityToggle={true}
            onEndReached={handleLoadMore}
            rowHeight={ROW_HEIGHT}
            afterLastRow={
              loadingMore ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : hasMore ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <Button onClick={handleLoadMore}>Load More</Button>
                </Box>
              ) : null
            }
          />
        </Box>
      </Box>

      {/* Modals */}
      {auditModalOpen && (
        <ShipmentAuditModal
          open={auditModalOpen}
          onClose={() => setAuditModalOpen(false)}
          shipmentId={selectedShipmentId}
        />
      )}

      {editModalOpen && (
        <EditShipmentModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          shipmentId={selectedShipmentId}
          onSuccess={handleRefresh}
        />
      )}

      {simulateDialogOpen && (
        <SimulateRemindersDialog
          open={simulateDialogOpen}
          onClose={() => setSimulateDialogOpen(false)}
          shipmentId={selectedShipmentId}
        />
      )}
    </>
  );
}
